import { useCallback, useEffect, useReducer, useRef, type RefObject } from 'react';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';

/**
 * Provides helpers to access the OverlayScrollbars viewport element.
 *
 * The viewport is the actual scrollable DOM node created by OverlayScrollbars.
 * Use `getViewport` as the `getScrollElement` for `useVirtualizer`, and
 * `scrollTo` for programmatic scroll resets.
 *
 * Note: OverlayScrollbars initializes asynchronously in a `useEffect`.
 * This hook polls for the osInstance and forces a re-render once available,
 * so the virtualizer's layout-effect cycle picks up the viewport element.
 */
export function useScrollViewport(osRef: RefObject<OverlayScrollbarsComponentRef | null>) {
  const [, forceRender] = useReducer((c: number) => c + 1, 0);
  const hasInitializedRef = useRef(false);

  // Poll for OverlayScrollbars initialization.
  // The osInstance initializes asynchronously after mount, so we need to
  // wait for it and then force a re-render so the virtualizer can pick it up.
  useEffect(() => {
    // Already initialized, no need to poll
    if (hasInitializedRef.current) return;

    const checkInstance = () => {
      if (osRef.current?.osInstance()) {
        hasInitializedRef.current = true;
        forceRender();
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkInstance()) return;

    // Poll with requestAnimationFrame until initialized (typically 1-2 frames)
    let rafId: number;
    let attempts = 0;
    const maxAttempts = 30; // ~500ms at 60fps

    const poll = () => {
      attempts++;
      if (checkInstance()) return;
      if (attempts < maxAttempts) {
        rafId = requestAnimationFrame(poll);
      }
    };

    rafId = requestAnimationFrame(poll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [osRef]);

  const getViewport = useCallback(
    () => osRef.current?.osInstance()?.elements().viewport ?? null,
    [osRef],
  );

  const scrollTo = useCallback(
    (options: ScrollToOptions) => {
      getViewport()?.scrollTo(options);
    },
    [getViewport],
  );

  return { getViewport, scrollTo };
}
